import { apiRequest } from '../../shared/api/base'
import type { PluginSettingDef, PluginConnectionDetails, PluginFeatureGroup } from './types'

// ─── API response types ─────────────────────────────────────

export interface CatalogPluginDto {
  id: string
  name: string
  description: string
  icon: string
  color: string
  version: string
  author: string
  category: string
  verified: boolean
  installed: boolean
  enabled: boolean
  requiredTier: string | null
  hasScreen: boolean
  authRequired: boolean
  connected: boolean | null
  connectionDetails?: PluginConnectionDetails | null
  featureGroups?: PluginFeatureGroup[] | null
}

export interface InstalledPluginDto {
  id: string
  name: string
  description: string
  icon: string
  color: string
  version: string
  author: string
  category: string
  enabled: boolean
  settings: Record<string, unknown>
  settingDefs: PluginSettingDef[]
  suggestions: Array<{ id: string; text: string }>
  installedAt: string
  connected: boolean | null
  connectionDetails?: PluginConnectionDetails | null
  defaultConnectionId?: string | null
  supportsRunThrough?: boolean
  runThroughLastRunAt?: string | null
  meta?: Record<string, unknown>
}

// ─── API calls ──────────────────────────────────────────────

export async function getCatalog(opts?: { category?: string; search?: string }): Promise<{ plugins: CatalogPluginDto[] }> {
  const params = new URLSearchParams()
  if (opts?.category) params.set('category', opts.category)
  if (opts?.search) params.set('search', opts.search)
  const qs = params.toString()
  return apiRequest<{ plugins: CatalogPluginDto[] }>(`/plugins${qs ? `?${qs}` : ''}`)
}

export async function getInstalled(): Promise<{ plugins: InstalledPluginDto[] }> {
  return apiRequest<{ plugins: InstalledPluginDto[] }>('/plugins/installed')
}

export async function installPlugin(pluginId: string): Promise<{ pluginId: string; installed: boolean; enabled: boolean; settings: Record<string, unknown> }> {
  return apiRequest(`/plugins/${pluginId}/install`, { method: 'POST' })
}

export async function uninstallPlugin(pluginId: string): Promise<{ pluginId: string; installed: boolean }> {
  return apiRequest(`/plugins/${pluginId}/install`, { method: 'DELETE' })
}

export async function togglePlugin(pluginId: string, enabled: boolean): Promise<{ pluginId: string; enabled: boolean }> {
  return apiRequest(`/plugins/${pluginId}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  })
}

export async function updatePluginSettings(pluginId: string, settings: Record<string, unknown>): Promise<{ pluginId: string; settings: Record<string, unknown> }> {
  return apiRequest(`/plugins/${pluginId}/settings`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  })
}

export async function setDefaultConnection(pluginId: string, connectionId: string): Promise<{ pluginId: string; defaultConnectionId: string }> {
  return apiRequest(`/plugins/${pluginId}/default-connection`, {
    method: 'PATCH',
    body: JSON.stringify({ connectionId }),
  })
}

export async function runPluginRunThrough(pluginId: string): Promise<{ ok: boolean; dispatched: boolean }> {
  return apiRequest(`/plugins/${pluginId}/run-through`, { method: 'POST' })
}
